import React, { useState } from "react";
import { C, IMGS } from "../constants";
import { Reveal, Eyebrow, H, P, Btn, Sec } from "../components/UI";

function TimelineItem({ year, title, body, i, last }) {
  return (
    <Reveal delay={i * 100}>
      <div style={{ display: "flex", gap: 22, marginBottom: last ? 0 : 44 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
          <div style={{ width: 12, height: 12, background: C.accent, borderRadius: "50%", marginTop: 5, flexShrink: 0 }} />
          {!last && <div style={{ width: 2, flex: 1, background: C.border, marginTop: 6 }} />}
        </div>
        <div>
          <span style={{ display: "inline-block", fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 12, color: C.accent, background: C.accentLight, padding: "2px 10px", marginBottom: 8, letterSpacing: 1 }}>{year}</span>
          <h4 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 17, color: C.black, marginBottom: 8 }}>{title}</h4>
          <P size="sm">{body}</P>
        </div>
      </div>
    </Reveal>
  );
}

export default function AboutPage({ setPage }) {
  const milestones = [
    { year: "2022", title: "The Spark",   body: "Armed with a Samsung phone and a gimbal, Sreenithi began shooting and editing solo — learning every discipline from scratch. No team, no resources, no shortcuts. Pure craft." },
    { year: "2023", title: "First Team",  body: "Growing demand led to the onboarding of a first editor and cinematographer. The brand expanded in quality and creative depth, gaining serious attention across Chennai." },
    { year: "2024", title: "Scaling Up",  body: "Two more editors joined, enabling larger and more complex productions. A Visual Communication background gave the team a distinctive edge in concept and execution." },
    { year: "2025", title: "Today",       body: "A full team of skilled professionals delivering precision, artistry, and strategy on every project. Tharagai Productions is now one of Chennai's most sought-after creative houses." },
  ];

  const values = [
    { no: "01", title: "Story Before Style",       desc: "A beautiful frame means nothing without a compelling narrative behind it. We always start with story." },
    { no: "02", title: "Craft Over Speed",          desc: "We take the time to get it right. Every script, every shot, every cut is considered — never rushed." },
    { no: "03", title: "Conversion is the Goal",    desc: "Beautiful work must perform. We create content that achieves your business goals measurably." },
    { no: "04", title: "Partnership, Not Vendor",   desc: "We invest deeply in understanding your brand. Your success is our north star on every project." },
  ];

  return (
    <div style={{ paddingTop: 84 }}>
      {/* Hero */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: "88vh", display: "flex", alignItems: "flex-end", background: C.black }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMGS.aboutHero})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.38 }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(10,10,10,0.97) 42%, rgba(10,10,10,0.3))" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(10,10,10,0.82) 0%, transparent 50%)" }} />
        <div style={{ position: "relative", padding: "0 clamp(20px,7vw,120px) 100px", maxWidth: 920 }}>
          <Reveal>
            <Eyebrow text="Our Story" />
            <H size="xl" color={C.white} style={{ marginTop: 14, marginBottom: 22 }}>Born from Passion.<br />Built on Purpose.</H>
            <P size="lg" color="rgba(255,255,255,0.52)" style={{ maxWidth: 500, lineHeight: 1.85 }}>
              The story of Tharagai Productions is one of grit, creativity, and relentless growth — from a single phone to a full production house.
            </P>
          </Reveal>
        </div>
      </section>

      {/* Founder Section */}
      <Sec bg={C.white}>
        <div className="tp-split" style={{ gap: 80, alignItems: "start" }}>
          <Reveal from="left">
            <div style={{ position: "relative" }}>
              <div style={{ aspectRatio: "3/4", backgroundImage: `url(${IMGS.director})`, backgroundSize: "cover", backgroundPosition: "center top" }} />
              <div style={{ position: "absolute", bottom: -28, right: -20, background: C.accent, color: C.white, padding: "24px 26px", maxWidth: 240 }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 26, lineHeight: 1.05, marginBottom: 6 }}>"</div>
                <P size="sm" color="rgba(255,255,255,0.9)" style={{ lineHeight: 1.7 }}>Content that doesn't just look good — content that sells.</P>
              </div>
              <div style={{ position: "absolute", top: 120, left: -18, background: C.black, color: C.white, padding: "13px 18px" }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 14, letterSpacing: 1 }}>SREENITHI</div>
                <div style={{ fontFamily: "'Poppins', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.45)", letterSpacing: 2, marginTop: 2 }}>FOUNDER & DIRECTOR</div>
              </div>
            </div>
          </Reveal>
          <Reveal from="right" style={{ paddingTop: 16 }}>
            <Eyebrow text="About the Founder" />
            <H size="lg" style={{ marginTop: 12, marginBottom: 32 }}>A Small-Town Girl<br />With Big Ambitions</H>
            <div style={{ borderLeft: `3px solid ${C.border}`, paddingLeft: 28 }}>
              {milestones.map((m, i) => (
                <TimelineItem key={m.year} {...m} i={i} last={i === milestones.length - 1} />
              ))}
            </div>
          </Reveal>
        </div>
      </Sec>

      {/* Philosophy */}
      <Sec bg={C.surface}>
        <Reveal style={{ marginBottom: 52, textAlign: "center" }}>
          <Eyebrow text="Our Philosophy" center />
          <H size="xl" style={{ marginTop: 12 }}>What Drives<br />Everything We Do</H>
        </Reveal>
        <Reveal>
          <div style={{ background: C.black, padding: "52px clamp(28px,5.5vw,72px)", marginBottom: 52, position: "relative", overflow: "hidden" }}>
            <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: "clamp(70px,12vw,150px)", color: "rgba(255,255,255,0.04)", position: "absolute", top: -10, left: 20, lineHeight: 1 }}>"</div>
            <p style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: "clamp(18px,2.8vw,32px)", lineHeight: 1.45, position: "relative", maxWidth: 700, color: C.white }}>
              Every brand has a story. Not every brand knows how to tell it in a way that{" "}
              <span style={{ color: C.accent }}>moves people and drives sales.</span>
              {" "}That's where we come in.
            </p>
          </div>
        </Reveal>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 3 }}>
          {values.map((v, i) => (
            <Reveal key={v.no} delay={i * 90}>
              <div style={{ borderTop: `3px solid ${C.accent}`, padding: "34px 26px 38px", background: C.white, border: `1px solid ${C.border}`, borderTop: `3px solid ${C.accent}` }}>
                <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 38, color: "rgba(0,0,0,0.06)", lineHeight: 1, marginBottom: 14, letterSpacing: -2 }}>{v.no}</div>
                <h3 style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: 17, color: C.black, marginBottom: 12 }}>{v.title}</h3>
                <P size="sm">{v.desc}</P>
              </div>
            </Reveal>
          ))}
        </div>
      </Sec>

      {/* Team Split */}
      <section className="tp-split">
        <div style={{ background: C.accent, padding: "80px clamp(28px,5.5vw,76px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Reveal>
            <Eyebrow text="The Team" />
            <H size="lg" color={C.white} style={{ marginTop: 12, marginBottom: 22 }}>A Team That<br />Bleeds Creativity</H>
            <P size="md" color="rgba(255,255,255,0.78)" style={{ marginBottom: 14 }}>
              Sreenithi leads editors, cinematographers, strategists, and content creators — each bringing expertise and artistic flair to every single project.
            </P>
            <P size="md" color="rgba(255,255,255,0.58)" style={{ marginBottom: 36 }}>
              We're not a factory. Every project gets personal attention, from the first brief to the final export.
            </P>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {[["5+", "Team Members"], ["3+", "Years Together"], ["50+", "Brand Clients"], ["200+", "Projects Done"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 900, fontSize: 32, color: C.white, lineHeight: 1 }}>{v}</div>
                  <P size="sm" color="rgba(255,255,255,0.55)" style={{ marginTop: 4 }}>{l}</P>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
        <div style={{ position: "relative", overflow: "hidden", minHeight: 480 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMGS.team})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          <div style={{ position: "absolute", inset: 0, background: "rgba(10,10,10,0.18)" }} />
        </div>
      </section>

      {/* CTA */}
      <Sec bg={C.white} style={{ textAlign: "center" }}>
        <Reveal>
          <H size="xl" style={{ marginBottom: 18 }}>Ready to Create Something Iconic?</H>
          <P size="lg" color={C.muted} style={{ maxWidth: 460, margin: "0 auto 36px" }}>
            Let's collaborate on your next brand film, campaign, or social media strategy.
          </P>
          <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Btn variant="solid" onClick={() => { setPage("contact"); window.scrollTo(0, 0); }}>Work With Us</Btn>
            <Btn variant="outline" onClick={() => { setPage("work"); window.scrollTo(0, 0); }}>See Our Work</Btn>
          </div>
        </Reveal>
      </Sec>
    </div>
  );
}
